// usePolicyAcceptance — the clickwrap layer (LAUNCH_READINESS_PLAN F2). Reads the
// user's per-policy current-vs-accepted status (`get_my_policy_status`) and
// records an acceptance of the exact version shown (`record_policy_acceptance`,
// the sole append-only write path). The accept-gate (F3) consumes `allAccepted`.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

export type PolicyType = "terms" | "privacy" | "community_guidelines";

// Policies a new signup attests to — all three now have live pages
// (legal/*.draft.md rendered at /terms, /privacy, /community-guidelines).
const SIGNUP_POLICIES: PolicyType[] = ["terms", "privacy", "community_guidelines"];
// localStorage marker set at signup-submit; the bootstrap records the server-side
// acceptance once a session exists (bridges the email-confirmation gap where
// there's no auth.uid() at signUp time).
const PENDING_ACK_KEY = "rb_policy_ack_pending";

export interface PolicyStatus {
  policy_type: PolicyType;
  current_version: string;
  accepted_version: string | null;
  accepted: boolean;
  accepted_at: string | null;
}

const POLICY_STATUS_KEY = ["policy-status"] as const;

/** Per-policy: current version vs the user's latest accepted version.
 *  `enabled` gates the query off for anonymous callers (the RPC is
 *  authenticated-only, so running it without a session just errors). */
export function usePolicyStatus(enabled = true) {
  return useQuery({
    queryKey: POLICY_STATUS_KEY,
    enabled,
    queryFn: async (): Promise<PolicyStatus[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("get_my_policy_status");
      if (error) throw error;
      return (data ?? []) as PolicyStatus[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Record acceptance of the EXACT version the user was shown (not "current", to
 *  avoid recording assent to a version they never saw if it bumped mid-view). */
export function useAcceptPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { policyType: PolicyType; version: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("record_policy_acceptance", {
        p_policy_type: vars.policyType,
        p_policy_version: vars.version,
        p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: POLICY_STATUS_KEY }),
  });
}

/** Convenience for the accept-gate: are ALL current policies accepted? */
export function useAllPoliciesAccepted() {
  const q = usePolicyStatus();
  const list = q.data ?? [];
  const allAccepted = list.length > 0 && list.every((p) => p.accepted);
  const pending = list.filter((p) => !p.accepted);
  return { ...q, allAccepted, pending };
}

/** Call at signup-submit (after the user ticks the agree box). Drops a marker so
 *  the server-side acceptance is recorded as soon as a session exists — even
 *  when email confirmation defers the session past the signUp call. */
export function markSignupPolicyAck() {
  try {
    localStorage.setItem(PENDING_ACK_KEY, "1");
  } catch {
    /* private mode / storage disabled — the bootstrap simply won't fire */
  }
}

/** Mounted once app-wide (PolicyAckBootstrap). When a session appears AND the
 *  signup marker is set, records acceptance of the signup policies at their
 *  current versions, then clears the marker. Existing users (no marker) are
 *  never auto-accepted. */
export function usePolicyAckBootstrap() {
  const { session } = useUserProfile();
  const [pending, setPending] = useState(false);
  useEffect(() => {
    try {
      setPending(localStorage.getItem(PENDING_ACK_KEY) === "1");
    } catch {
      setPending(false);
    }
  }, [session]);

  const enabled = !!session && pending;
  const { data: status } = usePolicyStatus(enabled);
  const accept = useAcceptPolicy();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled || !status || ranRef.current) return;
    ranRef.current = true;
    void (async () => {
      try {
        for (const t of SIGNUP_POLICIES) {
          const s = status.find((x) => x.policy_type === t);
          if (s && !s.accepted) {
            await accept.mutateAsync({ policyType: t, version: s.current_version });
          }
        }
      } finally {
        try {
          localStorage.removeItem(PENDING_ACK_KEY);
        } catch {
          /* ignore */
        }
        setPending(false);
      }
    })();
  }, [enabled, status, accept]);
}
